import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CheckIcon } from '@shared/ui';
import type { StarterModule } from '../model/flow';
import { useOnboardingStarterQuery } from '../model/queries';
import { ModulePicker } from './ModulePicker';
import { ButtonSpinner, OnboardingShell } from './OnboardingShell';

/** Что выбрал ученик на экране: `sourceId` — официальный модуль (нет — сервер возьмёт стартовый). */
export interface StarterChoice {
  sourceId?: string;
  /** Взял не стартовый модуль, а «другую тему». */
  alternative: boolean;
}

interface OnboardingIntroProps {
  /** Какой запрос идёт: «Начать», «Играть», «Создать карточку» или «Пропустить»; все кнопки на это время заблокированы. */
  busy: 'start' | 'skip' | 'mission' | 'card' | null;
  error: string | null;
  onStart: (choice: StarterChoice) => void;
  onSkip: () => void;
  /** Миссия дня доступна — экран «С чего начнём?»: миссия или 5 слов; `first` — какая карточка первой. */
  mission?: { first: boolean } | null;
  onMission?: () => void;
  /** «Свои слова» (2026-11-02): карточка на своё слово; `first` — первой на экране (шёл за карточками). */
  card?: { first: boolean } | null;
  onCard?: () => void;
}

type Option = 'module' | 'mission' | 'card';

const POINTS = ['pointDictionary', 'pointTopics', 'pointQuick'] as const;

/**
 * Экран 1 знакомства. С 2026-10-30 — «С чего начнём?»: миссия дня в Лондоне или «5 слов за 2 минуты» со стартовым
 * модулем (решение владельца: новички бросали первую тренировку на первом же вводе перевода, а пришедший из ролика
 * прошёл миссию целиком). С 2026-11-02 третий вариант — «Свои слова»: карточка на своё слово (пришедшие из поиска на
 * /ai-kartochki бросали тренировку готовой темы). Первой стоит карточка по источнику: шёл за карточками — «Свои слова»,
 * из демо игры — миссия, иначе — 5 слов; у первой кнопка главная. Ни миссии, ни своих слов — прежний экран: три тезиса,
 * карточка модуля, «Начать: 5 слов за 2 минуты» и «Пропустить».
 */
export function OnboardingIntro({ busy, error, onStart, onSkip, mission, onMission, card, onCard }: OnboardingIntroProps) {
  const { t } = useTranslation();
  const starterQuery = useOnboardingStarterQuery();
  const starter = starterQuery.data?.starter ?? null;
  const options = useMemo<StarterModule[]>(
    () => (starterQuery.data?.starter ? [starterQuery.data.starter, ...starterQuery.data.alternatives] : []),
    [starterQuery.data],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const selected = options.find((m) => m.id === selectedId) ?? starter;
  const isBusy = busy !== null;
  const canPick = options.length > 1;
  const missionOpen = !!mission && !!onMission;
  const cardOpen = !!card && !!onCard;
  const choice = missionOpen || cardOpen;
  const order: Option[] =
    cardOpen && card?.first
      ? ['card', 'module', 'mission']
      : missionOpen && mission?.first
        ? ['mission', 'module', 'card']
        : ['module', 'mission', 'card'];
  const shown = order.filter((o) => o === 'module' || (o === 'mission' ? missionOpen : cardOpen));
  const primary = shown[0];

  const start = () =>
    onStart({
      sourceId: selected?.id,
      alternative: !!selected && !!starter && selected.id !== starter.id,
    });

  const startButton = (primary: boolean) => (
    <Button
      size="lg"
      fullWidth
      variant={primary ? 'primary' : 'outline'}
      className={choice ? 'mt-4' : undefined}
      onClick={start}
      disabled={isBusy}
      aria-busy={busy === 'start'}
    >
      {busy === 'start' && <ButtonSpinner />}
      {busy === 'start' ? t('onboarding.intro.starting') : t('onboarding.intro.start')}
    </Button>
  );

  const moduleCard = (
    <section className="rounded-card border border-line bg-card p-4 sm:p-5">
      <div className="flex min-h-[28px] items-center justify-between gap-3">
        <h2 className="font-mono text-[12px] font-medium uppercase tracking-[0.14em] text-muted">
          {picking ? t('onboarding.intro.pickLabel') : t('onboarding.intro.moduleLabel')}
        </h2>
        {canPick && (
          <button
            type="button"
            onClick={() => setPicking((v) => !v)}
            disabled={isBusy}
            aria-expanded={picking}
            className="-my-2 inline-flex min-h-[44px] items-center whitespace-nowrap px-1 text-[14px] font-medium text-accent transition-colors hover:text-accent-hi disabled:cursor-not-allowed disabled:opacity-50"
          >
            {picking ? t('onboarding.intro.collapse') : t('onboarding.intro.otherTopic')}
          </button>
        )}
      </div>

      {starterQuery.isPending ? (
        <div className="mt-2 space-y-2" aria-hidden>
          <div className="h-7 w-3/4 animate-skeleton rounded-lg bg-elev" />
          <div className="h-5 w-1/3 animate-skeleton rounded-lg bg-elev" />
        </div>
      ) : !selected ? (
        // Тему не загрузили — «Начать» всё равно работает: модуль выберет сервер.
        <p className="mt-2 text-[14px] text-muted" role="status">
          {t('onboarding.intro.starterError')}{' '}
          <button
            type="button"
            onClick={() => void starterQuery.refetch()}
            disabled={starterQuery.isFetching}
            className="font-medium text-fg underline underline-offset-2 hover:text-accent disabled:opacity-50"
          >
            {t('onboarding.intro.starterRetry')}
          </button>
        </p>
      ) : picking ? (
        <div className="mt-3">
          <ModulePicker
            modules={options}
            selectedId={selected.id}
            disabled={isBusy}
            onSelect={(id) => {
              setSelectedId(id);
              setPicking(false);
            }}
          />
        </div>
      ) : (
        <div className="mt-1.5">
          <p className="font-serif text-[22px] font-medium leading-tight tracking-[-0.01em] text-fg sm:text-[24px]">
            {selected.name}
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-2 text-[14px] text-muted">
            {selected.cefrLevel && (
              <span className="rounded-pill bg-elev px-2 py-0.5 font-mono text-[12px] font-semibold tracking-[0.08em] text-muted">
                {selected.cefrLevel}
              </span>
            )}
            {selected.wordCount > 0 && <span>{t('onboarding.intro.words', { count: selected.wordCount })}</span>}
          </p>
        </div>
      )}
      {choice && startButton(primary === 'module')}
    </section>
  );

  const missionCard = missionOpen && (
    <section className="rounded-card border border-line bg-card p-4 sm:p-5">
      <h2 className="font-mono text-[12px] font-medium uppercase tracking-[0.14em] text-muted">
        {t('onboarding.intro.missionLabel')}
      </h2>
      <p className="mt-1.5 font-serif text-[22px] font-medium leading-tight tracking-[-0.01em] text-fg sm:text-[24px]">
        {t('onboarding.intro.missionTitle')}
      </p>
      <p className="mt-2 text-[14px] leading-snug text-muted">{t('onboarding.intro.missionText')}</p>
      <Button
        size="lg"
        fullWidth
        variant={primary === 'mission' ? 'primary' : 'outline'}
        className="mt-4"
        onClick={onMission}
        disabled={isBusy}
        aria-busy={busy === 'mission'}
      >
        {busy === 'mission' && <ButtonSpinner />}
        {busy === 'mission' ? t('onboarding.intro.missionStarting') : t('onboarding.intro.missionStart')}
      </Button>
    </section>
  );

  const cardCard = cardOpen && (
    <section className="rounded-card border border-line bg-card p-4 sm:p-5">
      <h2 className="font-mono text-[12px] font-medium uppercase tracking-[0.14em] text-muted">
        {t('onboarding.intro.cardLabel')}
      </h2>
      <p className="mt-1.5 font-serif text-[22px] font-medium leading-tight tracking-[-0.01em] text-fg sm:text-[24px]">
        {t('onboarding.intro.cardTitle')}
      </p>
      <p className="mt-2 text-[14px] leading-snug text-muted">{t('onboarding.intro.cardText')}</p>
      <Button
        size="lg"
        fullWidth
        variant={primary === 'card' ? 'primary' : 'outline'}
        className="mt-4"
        onClick={onCard}
        disabled={isBusy}
        aria-busy={busy === 'card'}
      >
        {busy === 'card' && <ButtonSpinner />}
        {busy === 'card' ? t('onboarding.intro.cardStarting') : t('onboarding.intro.cardStart')}
      </Button>
    </section>
  );

  const sections: Record<Option, ReactNode> = { module: moduleCard, mission: missionCard, card: cardCard };

  return (
    <OnboardingShell step={0}>
      <h1 className="text-balance font-serif text-[30px] font-medium leading-[1.12] tracking-[-0.02em] sm:text-[36px] nav:text-[44px]">
        {t(choice ? 'onboarding.intro.choiceTitle' : 'onboarding.intro.title')}
      </h1>
      {choice ? (
        <div className="mt-6 flex flex-col gap-3">
          {shown.map((option) => (
            <Fragment key={option}>{sections[option]}</Fragment>
          ))}
        </div>
      ) : (
        <>
          <ul className="mt-5 space-y-2.5">
            {POINTS.map((key) => (
              <li key={key} className="flex items-start gap-2.5 text-[16px] leading-snug text-fg">
                <CheckIcon className="mt-0.5 h-[18px] w-[18px] flex-none text-accent" aria-hidden />
                <span>{t(`onboarding.intro.${key}`)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7">{moduleCard}</div>
        </>
      )}

      <div className={choice ? 'mt-4 flex flex-col gap-3' : 'mt-7 flex flex-col gap-3'}>
        {!choice && startButton(true)}
        {error && (
          <p role="alert" className="rounded-xl bg-danger/15 px-3.5 py-2.5 text-[14px] leading-snug text-danger">
            {error}
          </p>
        )}
        <button
          type="button"
          onClick={onSkip}
          disabled={isBusy}
          aria-busy={busy === 'skip'}
          className="min-h-[44px] self-center px-4 text-[15px] font-medium text-muted transition-colors hover:text-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy === 'skip' ? t('onboarding.intro.skipping') : t(choice ? 'onboarding.intro.lookAround' : 'onboarding.skip')}
        </button>
      </div>
    </OnboardingShell>
  );
}
